import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Typography, Box, FormControlLabel, Checkbox, Button, MenuItem } from '@mui/material';
import { List as ListIcon } from '@mui/icons-material';
import API from '@utils/axios';
import FieldName from '@components/workflow/formdialog/FieldName';
import EnumDialog from '@components/workflow/enumdialog/EnumDialog';

const FieldProperties = ({ customerId, field, onFieldUpdate, availableLangs }) => {
    const { t, i18n } = useTranslation();
    const [enumDialogOpen, setEnumDialogOpen] = useState(false);
    const [enumDialogReadOnly, setEnumDialogReadOnly] = useState(false);
    const currentLangInputRef = useRef(null);
    const [externalEnums, setExternalEnums] = useState([]);
    const enumSeparator = '|||';

    const getCustomerExternalEnum = async (customerId, service, id) => {
        const response = await API.get(`/customers/${customerId}/services/${service}/enums/${id}`);
        // Enregistrer les valeurs de l'enum
        onFieldUpdate({
            ...field,
            enumService: service,
            enumId: id,
            enum: response.data
        });
    };

    const getCustomerExternalEnums = async (customerId) => {
        const response = await API.get(`/customers/${customerId}/services/enums`);
        setExternalEnums(response.data.enums);
    };

    useEffect(() => {
        if (customerId && customerId !== '-') {
            getCustomerExternalEnums(customerId);
        }
    }, [customerId]);

    // Déterminer le mode lecture seule de l'EnumDialog au chargement et lors des changements
    useEffect(() => {
        if (!field) return;
        if (!hasEnum(field.type)) {
            setEnumDialogReadOnly(false);
            return;
        }
        const isExternalEnum = Boolean(field.enumService && field.enumId);
        setEnumDialogReadOnly(isExternalEnum);
    }, [field, field?.type, field?.enumService, field?.enumId]);

    const hasEnum = (type) => {
        return type === 'select' || type === 'selectmultiple' || type === 'radios' || type === 'checkboxes';
    };

    const handleNameChange = (event) => {
        onFieldUpdate({
            ...field,
            name: event.target.value
        });
    };

    const handleLabelChange = (langKey) => (event) => {
        onFieldUpdate({
            ...field,
            label: {
                ...field.label,
                [langKey]: event.target.value
            }
        });
    };

    const handleHelperTextChange = (langKey) => (event) => {
        onFieldUpdate({
            ...field,
            helperText: {
                ...field.helperText,
                [langKey]: event.target.value
            }
        });
    };

    const handleRequiredChange = (event) => {
        onFieldUpdate({
            ...field,
            required: event.target.checked
        });
    };

    const handleTypeChange = (event) => {
        const newType = event.target.value;
        onFieldUpdate({
            ...field,
            type: newType,
            // Si le nouveau type ne supporte pas les listes, on nettoie les infos d'enum
            ...(hasEnum(newType) ? {} : { enum: undefined, enumService: undefined, enumId: undefined })
        });
    };
    const handleEnumTypeChange = (event) => {
        const value = event.target.value;
        if (value === 'custom') {
            setEnumDialogReadOnly(false);
            onFieldUpdate({
                ...field,
                enum: {"001": {}, "002": {}, "003": {}},
                enumService: undefined,
                enumId: undefined
            });
            return;
        }
        // value est du type `${service}|||${id}`
        setEnumDialogReadOnly(true);
        const [service, id] = String(value).split(enumSeparator);
        onFieldUpdate({
            ...field,
            enumService: service,
            enumId: id,
        });
        // Récupérer les valeurs de l'enum
        getCustomerExternalEnum(customerId, service, id);
    };
    return field ? (
        <>
            <Box>
                <Typography variant="subtitle1" gutterBottom>
                    {field.type === 'info' ? t('Information') : <FieldName field={field} />}
                </Typography>

                {/* Sélecteur de type de champ */}
                <Box mt={1}>
                    <TextField
                        select
                        fullWidth
                        label={t('Field type')}
                        value={field.type || 'text'}
                        onChange={handleTypeChange}
                        size="small"
                        variant="standard"
                    >
                        <MenuItem value="text">{t('Text')}</MenuItem>
                        <MenuItem value="textarea">{t('Text Area')}</MenuItem>
                        <MenuItem value="checkbox">{t('Checkbox')}</MenuItem>
                        <MenuItem value="switch">{t('Switch')}</MenuItem>
                        <MenuItem value="select">{t('Dropdown')}</MenuItem>
                        <MenuItem value="selectmultiple">{t('Multiple dropdown')}</MenuItem>
                        <MenuItem value="radios">{t('Radio buttons')}</MenuItem>
                        <MenuItem value="checkboxes">{t('Checkbox group')}</MenuItem>
                        <MenuItem value="date">{t('Date')}</MenuItem>
                        <MenuItem value="datetime">{t('Date and time')}</MenuItem>
                        <MenuItem value="info">{t('Information')}</MenuItem>
                    </TextField>
                </Box>

                {field.type === 'info' ? (
                    <>
                        <Box mt={1}>
                            <TextField
                                fullWidth
                                label={t('Technical name')}
                                value={field.name}
                                onChange={handleNameChange}
                                size="small"
                                variant="standard"
                            />
                        </Box>

                        {/* Labels dans différentes langues */}
                        {availableLangs.map(langKey => (
                            <Box mt={2} key={langKey}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    inputRef={langKey === i18n.language ? currentLangInputRef : null}
                                    label={t('Information message') + ' (' + t('lang.' + langKey) + ')'}
                                    value={field.label[langKey] || ''}
                                    onChange={handleLabelChange(langKey)}
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                        ))}

                    </>
                ) : (
                    <>
                        <Box mt={1}>
                            <TextField
                                fullWidth
                                label={t('Technical name')}
                                value={field.name}
                                onChange={handleNameChange}
                                size="small"
                                variant="standard"
                            />
                        </Box>

                        {hasEnum(field.type) && (
                            <Box mt={1}>
                                <TextField
                                    select
                                    fullWidth
                                    label={t('Values list')}
                            value={(field.enumService && field.enumId) ? (field.enumService + enumSeparator + field.enumId) : 'custom'}
                                    onChange={handleEnumTypeChange}
                                    size="small"
                                    variant="standard"
                                >
                                    <MenuItem value="custom">- {t('Use custom values')} -</MenuItem>
                                    {externalEnums.map((enumRow) => (
                                        <MenuItem
                                            key={enumRow.service + enumSeparator + enumRow.id}
                                            value={enumRow.service + enumSeparator + enumRow.id}>
                                            {enumRow.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        )}

                        {hasEnum(field.type) && (
                            <Button
                                onClick={() => setEnumDialogOpen(true)}
                                variant="contained"
                                color={enumDialogReadOnly ? "default" : "primary"}
                                sx={{
                                    mt: 2,
                                    textTransform: 'none'
                                }}
                                size="small"
                                startIcon={<ListIcon />}
                            >
                                {enumDialogReadOnly ? t('View field values') : t('Configure field values')}
                            </Button>
                        )}

                        {/* Labels dans différentes langues */}
                        {availableLangs.map(langKey => (
                            <Box mt={2} key={langKey}>
                                <TextField
                                    fullWidth
                                    inputRef={langKey === i18n.language ? currentLangInputRef : null}
                                    label={t('Label') + ' (' + t('lang.' + langKey) + ')'}
                                    value={field.label[langKey] || ''}
                                    onChange={handleLabelChange(langKey)}
                                    size="small"
                                    variant="standard"
                                />
                            </Box>
                        ))}

                        {/* Helper text dans différentes langues */}
                        {availableLangs.map(langKey => (
                            <Box mt={2} key={langKey}>
                                <TextField
                                    fullWidth
                                    label={t('Helper text') + ' (' + t('lang.' + langKey) + ')'}
                                    value={field.helperText[langKey] || ''}
                                    onChange={handleHelperTextChange(langKey)}
                                    size="small"
                                    variant="standard"
                                />
                            </Box>
                        ))}

                        <Box mt={1}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={field.required || false}
                                        onChange={handleRequiredChange}
                                        size="small"
                                    />
                                }
                                label={t('Required field')}
                            />
                        </Box>

                    </>
                )}

            </Box>
            {enumDialogOpen && (
                <EnumDialog
                    langs={availableLangs}
                    initialData={field.enum}
                    readOnly={enumDialogReadOnly}
                    onSubmit={(enumData) => {
                        onFieldUpdate({
                            ...field,
                            enum: enumData
                        });
                        setEnumDialogOpen(false);
                    }}
                    onClose={() => setEnumDialogOpen(false)}
                />
            )}
        </>
    ) : null;
};

export default FieldProperties;
